import type { CatalogRatioGroupData } from '@gql/graphql';
import { RatioTypeEnum } from '@gql/graphql';
import { Grid, IconSearch, Select, TextField } from 'uikit';

import { GridItem } from '@consta/uikit/Grid';
import type { TextFieldPropOnChange } from '@consta/uikit/TextField';
import { useMemo } from 'react';

import type { TRatioTypeSelectItem } from '../../../MassRatioApplication/types';
import Styles from '../../addRatiosModal.module.css';
import { ratioTypesSelectItems } from '../../constants';

type TAddCoefficientModalToolbarProps = {
    ratioGroups: CatalogRatioGroupData[];
    selectedCatalogRatioGroup: CatalogRatioGroupData | null;
    onSelectCatalogRatioGroupId: (value: CatalogRatioGroupData | null) => void;
    selectedRatioType: RatioTypeEnum | null;
    setSelectedRatioType?: (value: TRatioTypeSelectItem | null) => void;
    canSearch?: boolean;
    isSearchDisabled?: boolean;
    searchString?: string | null;
    handleChangeSearchString?: TextFieldPropOnChange;
    getIsRatioGroupItemDisabled?: (group?: CatalogRatioGroupData) => boolean;
    ratioGroupSelectDisabled?: boolean;
    availableRatioTypes?: RatioTypeEnum[];
};

export const AddRatiosModalToolbar = (props: TAddCoefficientModalToolbarProps) => {
    const {
        selectedRatioType,
        setSelectedRatioType,
        ratioGroups,
        selectedCatalogRatioGroup,
        onSelectCatalogRatioGroupId,
        canSearch = true,
        isSearchDisabled = false,
        searchString,
        handleChangeSearchString,
        getIsRatioGroupItemDisabled,
        ratioGroupSelectDisabled,
        availableRatioTypes,
    } = props;

    const handleSelectCatalogRatioGroupId = ({ value }: { value: CatalogRatioGroupData | null }) => {
        onSelectCatalogRatioGroupId(value);
    };

    const isTechPartCoef = selectedRatioType === RatioTypeEnum.TechPartCoef;

    const ratioTypeSelectItems = useMemo(
        () =>
            !availableRatioTypes?.length
                ? ratioTypesSelectItems
                : ratioTypesSelectItems.filter((item) => availableRatioTypes.includes(item.id)),
        [availableRatioTypes],
    );

    const selectedRatioTypeObject = useMemo(
        () => ratioTypesSelectItems.find((item) => item.id === selectedRatioType) || null,
        [selectedRatioType],
    );

    const gridColsConfig = useMemo(
        () => (isTechPartCoef && !setSelectedRatioType ? 1 : 2),
        [isTechPartCoef, setSelectedRatioType],
    );

    return (
        <Grid cols={gridColsConfig} colGap="s" className={Styles.toolbarContainer}>
            {setSelectedRatioType && (
                <GridItem col={1}>
                    <Select
                        size="s"
                        items={ratioTypeSelectItems}
                        value={selectedRatioTypeObject}
                        onChange={({ value }) => setSelectedRatioType?.(value)}
                        placeholder="Тип коэффициента"
                    />
                </GridItem>
            )}
            {selectedRatioType !== RatioTypeEnum.TechPartCoef && (
                <GridItem>
                    <Select
                        size="s"
                        items={ratioGroups}
                        value={selectedCatalogRatioGroup}
                        onChange={handleSelectCatalogRatioGroupId}
                        placeholder="Раздел"
                        getItemLabel={(item) => item.name}
                        getItemDisabled={getIsRatioGroupItemDisabled}
                        disabled={ratioGroupSelectDisabled}
                    />
                </GridItem>
            )}
            {canSearch && (
                <GridItem style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                        className={Styles.searchRatios}
                        size="s"
                        placeholder="Поиск коэффициента в сборнике"
                        leftSide={IconSearch}
                        disabled={isSearchDisabled}
                        value={searchString}
                        onChange={handleChangeSearchString}
                    />
                </GridItem>
            )}
        </Grid>
    );
};

AddRatiosModalToolbar.displayName = 'AddRatiosModalToolbar';
